import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineStar,
  HiStar,
  HiOutlineArrowRight,
  HiOutlineClock,
} from "react-icons/hi2";
import { useNavLauncher } from "../hooks/useNavLauncher";
import "./HomeModuleLauncher.css";

function resolveLabel(path, entries, storedLabel) {
  const e = entries.find((x) => x.path === path);
  return e?.label || storedLabel || path;
}

/**
 * Home dashboard: favorites, recents, featured module tiles, link to full catalog.
 */
const FEATURED_CUSTOM_CAP = 16;
const FEATURED_DEFAULT_CAP = 8;

function HomeModuleLauncher({
  entries,
  featuresLoading,
  workspaceOverviewNavKeys = null,
}) {
  const navigate = useNavigate();
  const { recents, favorites, toggleFavorite, isFavorite } = useNavLauncher();

  const featured = useMemo(() => {
    const list = entries || [];
    if (workspaceOverviewNavKeys?.length) {
      const byId = new Map(list.map((e) => [e.id, e]));
      const out = [];
      for (const id of workspaceOverviewNavKeys) {
        if (out.length >= FEATURED_CUSTOM_CAP) break;
        const e = byId.get(id);
        if (e) out.push(e);
      }
      return out;
    }
    return list
      .filter(
        (e) =>
          e.id !== "apps-hub" &&
          e.group &&
          e.group !== "Navigation" &&
          e.group === "Overview",
      )
      .slice(0, FEATURED_DEFAULT_CAP);
  }, [entries, workspaceOverviewNavKeys]);

  const favoriteRows = useMemo(() => {
    // Only show favorites that the user currently has access to
    const allowedPaths = new Set(entries?.map(e => e.path) || []);
    return favorites
      .filter((f) => allowedPaths.has(f.path))
      .map((f) => ({
        ...f,
        label: resolveLabel(f.path, entries, f.label),
      }));
  }, [favorites, entries]);

  const recentRows = useMemo(() => {
    // Only show recents that the user currently has access to
    const allowedPaths = new Set(entries?.map(e => e.path) || []);
    return recents
      .filter((r) => r.path !== "/home" && r.path !== "/home/apps" && allowedPaths.has(r.path))
      .slice(0, 8)
      .map((r) => ({
        ...r,
        label: resolveLabel(r.path, entries, r.label),
      }));
  }, [recents, entries]);

  if (featuresLoading) {
    return (
      <section className="home-launcher" aria-busy="true">
        <p className="home-launcher-loading">Loading modules…</p>
      </section>
    );
  }

  return (
    <section className="home-launcher" aria-label="Module launcher">
      <div className="home-launcher-head">
        <h3 className="home-launcher-title">Your workspace</h3>
        <button
          type="button"
          className="home-launcher-catalog-btn"
          onClick={() => navigate("/home/apps")}
        >
          All modules
          <HiOutlineArrowRight aria-hidden />
        </button>
      </div>

      {favoriteRows.length > 0 && (
        <div className="home-launcher-block">
          <h4 className="home-launcher-subtitle">Favorites</h4>
          <ul className="home-launcher-tiles">
            {favoriteRows.map((f) => (
              <li key={f.path}>
                <button
                  type="button"
                  className="home-launcher-tile"
                  onClick={() => navigate(f.path)}
                >
                  <span className="home-launcher-tile-label">{f.label}</span>
                  <span className="home-launcher-tile-path">{f.path}</span>
                  <span
                    className="home-launcher-tile-fav"
                    role="button"
                    tabIndex={0}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      toggleFavorite(f.path, f.label);
                    }}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter" || ev.key === " ") {
                        ev.preventDefault();
                        ev.stopPropagation();
                        toggleFavorite(f.path, f.label);
                      }
                    }}
                    title="Remove from favorites"
                  >
                    <HiStar aria-hidden />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {recentRows.length > 0 && (
        <div className="home-launcher-block">
          <h4 className="home-launcher-subtitle">
            <HiOutlineClock className="home-launcher-inline-icon" aria-hidden />
            Recent
          </h4>
          <ul className="home-launcher-chips">
            {recentRows.map((r) => (
              <li key={r.path + r.ts}>
                <button
                  type="button"
                  className="home-launcher-chip"
                  onClick={() => navigate(r.path)}
                >
                  {r.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {featured.length > 0 && (
        <div className="home-launcher-block">
          <h4 className="home-launcher-subtitle">Overview</h4>
          <ul className="home-launcher-tiles">
            {featured.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="home-launcher-tile home-launcher-tile-featured"
                  onClick={() => navigate(item.path)}
                >
                  <span className="home-launcher-tile-label">{item.label}</span>
                  <span
                    className="home-launcher-tile-fav home-launcher-tile-fav-outline"
                    role="button"
                    tabIndex={0}
                    title={
                      isFavorite(item.path)
                        ? "Remove from favorites"
                        : "Add to favorites"
                    }
                    onClick={(ev) => {
                      ev.stopPropagation();
                      toggleFavorite(item.path, item.label);
                    }}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter" || ev.key === " ") {
                        ev.preventDefault();
                        ev.stopPropagation();
                        toggleFavorite(item.path, item.label);
                      }
                    }}
                  >
                    {isFavorite(item.path) ? (
                      <HiStar aria-hidden />
                    ) : (
                      <HiOutlineStar aria-hidden />
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="home-launcher-hint">
        Tip: Press <kbd className="home-launcher-kbd">⌘K</kbd> /{" "}
        <kbd className="home-launcher-kbd">Ctrl+K</kbd> to search any page.
      </p>
    </section>
  );
}

export default React.memo(HomeModuleLauncher);
